<template>
  <div class="scene">
    <div class="loading" v-if="loading">
      Loading...
    </div>

    <div v-if="error" class="error">
      {{ error }}
    </div>

    <div v-if="scene" class="scene-wrapper">
      <div class="content">

        <div class="main-content">
          <div class="scene-tile__preview-container">
            <img class="thumbnail" v-if="scene.thumbnail" :src="imageUrlFor(scene.thumbnail).ignoreImageParams().width(480)"/>
            <img class="screens" v-if="scene.screencaps" :src="imageUrlFor(scene.screencaps).ignoreImageParams()"/>
            <a class="play-button" v-if="scene.path" :href="'file://' + scene.path" target="_blank" @click="incrementViews(scene)"><img src="../assets/play-button.png" /></a>
          </div>

          <h1>
            <span class="title">{{scene.name}}</span>
            <a class="edit-button button" target="_blank" v-if="scene._id" :href="`http://localhost:3333/desk/scene;${scene._id}`">Edit</a>
          </h1>

          <div class="scene-details">
            <div class="details-sidebar">
              <ul v-if="scene.models" class="scene-inline__models">
                <li class="scene-inline__model" v-for="model in scene.models">
                  <router-link class="scene-inline__model" :to="{name: 'model', params: {id: model._id}}">
                    <div class="model__headshot safe-image" v-bind:style="[{ backgroundImage: `url(${imageUrlFor(model.imageHeadshot).width(480)})`}]"></div>
                    <div class="model__name">{{model.name}}</div>
                  </router-link>
                </li>
              </ul>
              <ul class="scene-sidebar__studios" v-if="scene.studios" >
                <li class="scene-sidebar__studio" v-for="studio in scene.studios" :style="{ 'border-color': studio.color, 'background-color': studio.color + '0d' }">
                  <router-link :to="{name: 'studio', params: {id: studio._id}}">
                    <div class="scenes__studio-name">{{studio.name}}</div>
                  </router-link>
                </li>
                <li class="scene-sidebar__date-wrapper">
                  <span class="scene-sidebar__date">{{new Date(scene.release).toLocaleDateString('en-us', { weekday:"long", year:"numeric", month:"short", day:"numeric"})}}</span>            
                </li>
                <li class="scene-sidebar__count-wrapper">
                  <span class="scene-sidebar__count">
                    Viewed {{ scene.views || 0 }} times.
                  </span>            
                </li>  
              </ul>
            </div>
            <div class="details-content" v-html="overviewHtml || '<p>This scene has no description.</p>'"></div>
          </div>
          
          <h2>Related Scenes</h2>
          <ul class="scene-list related-scenes">
            <li v-for="relatedScene in scene.relatedScenes" class="list__item" v-if="scene._id !== relatedScene._id" :key="scene._id">
              <SceneTile :scene='relatedScene'></SceneTile>
            </li>
          </ul>
        </div>
        <div class="sidebar">
          <div class="scene-sidebar__details">
            <ul v-if="scene.models" class="scene-sidebar__models">
              <li class="scene-sidebar__model safe-image" v-for="model in scene.models">
                <router-link :to="{name: 'model', params: {id: model._id}}">
                  <div class="model__headshot" v-bind:style="[{ backgroundImage: `url(${imageUrlFor(model.imageHeadshot).width(480)})`}]"></div>
                  <div class="model__name">{{model.name}}</div>
                </router-link>
              </li>
            </ul>
          </div>
        </div>        

      </div>
    </div>
  </div>
</template>

<script>
import sanity from "../sanity";
import blocksToHtml from "@sanity/block-content-to-html";
import imageUrlBuilder from "@sanity/image-url";
import SceneTile from '../components/SceneTile.vue';

const imageBuilder = imageUrlBuilder(sanity);

const query = `*[_type == "scene" && _id == $id] {
  _id,
  name,
  release,
  models,
  studios,
  thumbnail,
  screencaps,
  path,
  models[]->,
  studios[]->,
  description,
  views,
  "relatedScenes": *[_type=='scene']{
    _id,
    name,
    release,
    models,
    thumbnail,
    screencaps,
    models[]->,
    studios[]->,
  }[0...2]
}[0]
`;

export default {
  props: {
    id: {
      type: String
    }
  },
  name: "Scene",
  data() {
    return {
      loading: true,
      scene: null,
      viewCount: this.views
    };
  },
  components: {
    SceneTile
  },
  created() {
    this.fetchData();
  },
  watch: {
    $route: "fetchData"
  },
  methods: {
    imageUrlFor(source) {
      return imageBuilder.image(source);
    },
    fetchData() {
      this.error = this.scene = null;
      this.loading = true;

      const serializers = {
        types: {
          description: props => {
            const h = blocksToHtml.h;
            if (!props.node.description) {
              return false;
            }
            const descriptionArray = props.node.description.map(description => {
              return h("div", null, [
                h("p", null, description.description),
                h("span", null, "—"),
                h("a", { href: description.url }, description.author)
              ]);
            });
            return h("div", [
              h("h1", null, props.node.description),
              h("div", null, descriptionArray)
            ]);
          }
        }
      };

      sanity.fetch(query, { id: this.id }).then(
        scene => {
          this.loading = false;
          this.scene = scene;
          this.overviewHtml = blocksToHtml({
            blocks: scene.description,
            serializers: serializers,
            dataset: sanity.clientConfig.dataset,
            projectId: sanity.clientConfig.projectId
          });
        },
        error => {
          this.error = error;
        }
      );
    },
    incrementViews(scene) {
      console.log(scene.views);
      if (typeof scene.views !== 'number') {
        scene.views = 0;
      }
      scene.views++;
      sanity.patch(scene._id)
        .set({"views": scene.views})
        .commit()
    }
  }
};
</script>

<style scoped>
.content {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}
.sidebar {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  grid-column-end: span 1;
  padding: 0 0 0 36px;
  max-height: calc(56.25vw);
}
@media (min-width: 1000px) {
  .sidebar {
    display: flex;
  }
}
.sidebar img {
  max-width: 100%;
}
.scene-inline__models {
    display: flex;
    width: auto;
    margin: 0;
    padding: 0;
    justify-content: flex-start;
    align-items: center;
}
.scene-inline__model {
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 28px;
  margin-top: 8px;
  margin-right: 8px;
  margin-bottom: 12px;
  text-decoration: none;
}
.scene-inline__model .scene-inline__model-link {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-decoration: none;
    display: flex;
    height: 100%;
    text-decoration: none;
}
.scene-inline__model .model__headshot {
    display: block;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: var(--color-mono-5);
    top: 50%;
    left: 0;
    position: absolute;
    transform: translateY(-50%);
    transform-origin: center;
    transition: transform 0.5s ease-in-out, -webkit-transform 0.5s ease-in-out;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
}
.scene-inline__model .model__name {
    font-size: 12px;
    padding-left: 36px;
    white-space: nowrap;
    font-weight: 500;
    margin: 0;
}
@media (min-width: 1000px) {
  .scene-inline__models {
    display: none;
  }
}
.main-content {
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  grid-column-end: span 5;
  padding: 0;
}
@media (min-width: 1000px) {
  .main-content {
    grid-column-end: span 4;
  }
}
h1 {
  margin: 36px 0 12px;
  padding: 0 0 10px;
  border-bottom: solid 1px var(--color-mono-0);
  font-size: 1.375rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
h1 .date {
  font-size: 1rem;
  font-weight: 400;
  width: 100%;
  display: block;
  margin-top: -4px;
  text-align: right;
}
h1 span {
  display: block; 
  width: 100%; 
}
.main-content h2 {
  margin: 0 0 24px;
  padding: 0 0 12px;
  border-bottom: solid 1px var(--color-mono-0);
  font-size: 1rem;
}
.scene-list {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 20px 0 40px;
  width: 100%;
}
.list__item {
  width: 100%;
}
@media (max-width: 999px) {
  .related-scenes .list__item {
    width: calc(50% - 20px);
    
  }
}
.edit-button {
  margin: 0;
}
.scene {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  padding: 36px;
  grid-gap: 36px;
}
.scene-wrapper {
  width: 100%;
}
.scenes-list__model {
  display: block;
  font-size: 1rem;
}
.scene-tile__preview-container {
  font-size: 0;
  position: relative;
}
.scene-tile__preview-container .thumbnail {
  display: block;
  position: relative;
  width: 100%;
  height: auto;
  background: var(--color-mono-0);
  transition: opacity 0.35s ease-in-out;
  pointer-events: none;
  z-index: 2;
  border-radius: 5px;
}
.scene-tile__preview-container:hover .thumbnail {
  opacity: 0;
}
.scene-tile__preview-container .screens {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
}

.scene-tile__preview-container, .image-container {
  display: block;
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
  width: 100%;
  height: auto;
  border-radius: 5px;
  font-size: 0;
}
.scene-tile__preview-container:hover .screens {
  animation: screens 9s infinite step-end;
}
.scene-tile__title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  margin-bottom: 12px;
  border-bottom: solid 1px var(--color-mono-5);
  width: 100%;
}
.scene-tile__title-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.scene-sidebar__studios {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  margin: 0;
  padding: 0;
  font-size: 1.125rem;
}
.scene-sidebar__studios .scene-sidebar__studio {
  display: inline-flex;
  justify-content: center;
  font-size: 1.125rem;
  color: var(--color-mono-0);
  font-weight: 500;
}
.scene-sidebar__studios .scene-sidebar__studio a {
  font-size: 0.875rem;
  color: var(--color-mono-0);
  font-weight: 500;
  text-decoration: none;
}
.scene-sidebar__date-wrapper {
  list-style: none;
  margin-top: 24px;
  width: 100%;
}
@media (max-width: 999px) {
  .scene-sidebar__date-wrapper {
    margin-bottom: 0;
    padding-bottom: 0;
  }
}
.scene-sidebar__date {
  display: flex;
  width: 100%;
  font-size: 0.875rem;
  color: var(--color-mono-0);
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: solid 1px var(--color-mono-5);
}
@media (max-width: 999px) {
  .scene-sidebar__date {
    margin-bottom: 0;
    padding-bottom: 12px;
  }
}
.scene-sidebar__count-wrapper {
  display: flex;
  width: 100%;
  font-size: 0.875rem;
  color: var(--color-mono-0);
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: solid 1px var(--color-mono-5);
}
@media (max-width: 999px) {
  .scene-sidebar__count-wrapper {
    margin-bottom: 0;
    padding-bottom: 0;
  }
}
@media (max-width: 999px) {
  .scene-sidebar__count {
    margin-bottom: 0;
    padding-bottom: 0;
  }
}
.scene-tile__name {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-mono-0);
  text-decoration: none;
}
.scene-tile__date {
  font-size: 0.625rem;
  font-weight: 600;
}
.scene-tile__image-link {
  width: 100%;
}
.scene-sidebar__details {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  margin: 0;
  padding: 0;
  height: 100%;
  max-height: calc(44.25vw);
}
.scene-sidebar__models {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-gap: 16px;
  width: 100%;
  margin: 0;
  padding: 0;
  justify-content: flex-start;
  align-items: center;
}
.scene-sidebar__model {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;;
  height: auto;
  align-items: center;
  display: flex;
  margin-right: 18px;
  text-decoration: none;
}
.scene-sidebar__model a {
  text-decoration: none;
}
.model__name {
  margin-top: 8px;
  font-size: 0.875rem;
  color: var(--color-mono-0);
  font-weight: 500;
  text-decoration: none;
}
@media (max-width: 999px) {
  .model__name {
    margin: 8px 8px 0 0;
    padding-left: 0;
  }
}
.scene-sidebar__model:hover .scene-sidebar__model-image {
  transform: scale(1.075) translateY(-50%);
}
.scenes__model-title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: var(--color-mono-0);
  text-decoration: none;
}
.scene-sidebar__model-name {
  font-size: 12px;
  padding-left: 44px;
  white-space: nowrap;
  font-weight: 500;
}
.scene-sidebar__model-image {
  display: block;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-mono-5);
  top: 50%;
  left: 0;
  position: absolute;
  transform: translateY(-50%);
  transform-origin: center;
  transition: transform 0.5s ease-in-out;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}
.scenes__model-link {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--color-mono-0);
  text-decoration: none;
  display: flex;
  height: 100%;
}
.play-button {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  pointer-events: none;
}
.play-button img {
  width: 200px;
}
.scene-tile__preview-container:hover .play-button {
  opacity: 1;
  pointer-events: auto;
}
.scene-sidebar__studios {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  margin: 0;
  padding: 0;
  margin-top: 16px;
}
.scene-sidebar__studios .scene-sidebar__studio {
  display: inline-flex;
  justify-content: center;
  font-size: 1rem;
  color: var(--color-mono-0);
  font-weight: 500;
  border-width: 1px;
  border-style: solid;
  padding: 4px 12px;
  margin-right: 12px;
  border-radius: 5px;
}
.scene-sidebar__studios .scene-sidebar__studio a {
  font-size: 0.875rem;
  color: var(--color-mono-0);
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}
.scene-tile__name {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-mono-0);
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 14px 0;
  width: 100%;
  border-bottom: solid 1px var(--color-mono-5);
}
.edit-button {
  margin-right: 0;
  margin-left: 12px;
}
.scenes__model-name {
  font-size: 0.875rem;
  color: var(--color-mono-0);
  font-weight: 500;
  text-decoration: none;
}
.scene-details {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-bottom: 64px;
}
@media (max-width: 999px) {
  .scene-details {
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
  }
}
.details-sidebar {
  grid-column-end: span 2;
  width: 50%;
  min-width: 150px;
  max-width: 300px;
}
.details-content {
  grid-column-end: span 3;
}
</style>
